import React, { useCallback, useMemo } from 'react'
import { Navigate, Route, Routes, useNavigate, useParams, useLocation } from 'react-router'
import ProgressIndicator from '../progress_indicator'
import { StepConfig, StepListProps } from './types'

// url base_url/<step>
const isVisibleStep = (step: StepConfig) => !step.skip

const getCurrentStep = (step: string, steps: Array<StepConfig>) => {
  if (!step || !steps.find((s) => s.step === step)) return steps[0].step
  return step
}

const findCurrentVisibleStepIndex = (
  all: Array<StepConfig>,
  visible: Array<StepConfig>,
  currentStep: string
): number => {
  const visIndex = visible.reduce(
    (agg, { step }, index) => {
      agg[step] = index
      return agg
    },
    {} as Record<string, number>
  )

  let lastVisible = ''

  for (const { step, skip } of all) {
    if (!skip) lastVisible = step
    if (step === currentStep) return visIndex[lastVisible] ?? -1
  }

  return -1
}

export const Steps = <K extends string>({
  steps,
  paramKey,
  onComplete,
  nextLabel: defaultNextLabel,
  finalizeLabel,
  firstStepHint,
}: StepListProps<K>) => {
  const params = useParams()
  const { pathname, search } = useLocation()
  // const href = useHref()
  const currentStep = useMemo((): string => getCurrentStep(params['*'] || '', steps), [steps, params])
  const visibleSteps = useMemo(() => steps.filter(isVisibleStep), [steps])
  const visibleIndex = useMemo(
    () => findCurrentVisibleStepIndex(steps, visibleSteps, currentStep),
    [steps, visibleSteps, currentStep]
  )
  const navigate = useNavigate()

  const onStepComplete = useCallback(
    (data) => {
      const index = steps.findIndex(({ step }) => step === currentStep)
      // If we are on a visible step we want to proceed, otherwise we want to go back to the previous step
      const next =
        index > 0 && steps[index].skip
          ? steps.slice(0, index).filter(isVisibleStep).pop()
          : steps.slice(index + 1).filter(isVisibleStep)[0] || undefined
      if (!next) return onComplete()

      const nextUrl = `/${data.campaign.slug}/new/${next.step}${search}`

      navigate(nextUrl, { replace: true })
    },
    [currentStep, steps, onComplete, navigate, search]
  )

  const sanitizedUrl = pathname.replace(params[paramKey] ?? '', '').replace(/\/?$/, '')

  return (
    <>
      <ProgressIndicator steps={visibleSteps} currentStep={visibleIndex} />
      <Routes>
        {steps.map(({ step: slug, component: Component, nextLabel, ...step }, index) => (
          <Route
            key={slug}
            path={slug}
            element={
              <>
                {index === 0 && firstStepHint}
                <Component
                  onComplete={onStepComplete}
                  nextLabel={index < steps.length - 1 ? nextLabel ?? defaultNextLabel : finalizeLabel}
                  {...step}
                />
              </>
            }
          />
        ))}
        <Route index element={<Navigate replace to={`${sanitizedUrl}/${currentStep}${search}`} />} />
      </Routes>
    </>
  )
}

export default Steps
